import { Collapse } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { EmptyLiveRecords, LiveRecordFilters, SectionRecordings } from './components';
import { Wrapper, LabelWrapper, ClassCounter } from './LiveRecords.styles';

import { Icon } from '@/components/Icon';
import { LoadingBar } from '@/components/Loaders';
import { useMySubscriptions, useProfile, useStudentLiveRecordsByCourse } from '@/store';

const GENERAL_SECTION_NAME = 'GENERAL';

export const LiveRecords: React.FC = () => {
  const { t } = useTranslation('StudentCourseSummaryTranslations');
  const { selectedSubscription } = useMySubscriptions();
  const {
    roleCheck: { isStudent }
  } = useProfile();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const courseIdFromUrl = (params as { _id?: string })?._id;
  const sectionIdFromUrl = searchParams.get('sectionId');
  const courseIdOverride = !isStudent ? courseIdFromUrl : undefined;
  const { isLoading, liveRecords } = useStudentLiveRecordsByCourse(courseIdOverride);
  const [openSections, setOpenSections] = useState<Record<string, string>>({});

  useEffect(() => {
    if (sectionIdFromUrl) {
      setOpenSections({
        [sectionIdFromUrl]: sectionIdFromUrl
      });
    }
  }, [sectionIdFromUrl]);

  if (isStudent && !selectedSubscription) return null;

  if (isLoading) return <LoadingBar />;

  if (!liveRecords || liveRecords.length === 0) return <EmptyLiveRecords />;

  return (
    <Wrapper>
      {isStudent && (
        <LiveRecordFilters
          counts={{
            all: liveRecords.reduce((acc, s) => acc + (s.allCount ?? s.recordings.length), 0),
            seen: liveRecords.reduce((acc, s) => acc + (s.seenCount ?? 0), 0),
            not_seen: liveRecords.reduce((acc, s) => acc + (s.notSeenCount ?? 0), 0)
          }}
        />
      )}
      {liveRecords
        .filter(section => section.recordings.length > 0)
        .map(({ sectionId, sectionName, recordings }) => (
          <Collapse
            key={sectionId}
            destroyOnHidden
            activeKey={openSections[sectionId]}
            onChange={activeKey => {
              setOpenSections(prev => ({
                ...prev,
                [sectionId]: Array.isArray(activeKey) ? activeKey[0] : activeKey
              }));
            }}
            expandIcon={({ isActive }) => <Icon i={isActive ? 'ChevronUp' : 'ChevronDown'} />}
            expandIconPosition="end"
            items={[
              {
                key: sectionId,
                label: (
                  <LabelWrapper>
                    {sectionName === GENERAL_SECTION_NAME ? t(GENERAL_SECTION_NAME) : sectionName}
                    <ClassCounter>{t('CLASS_COUNTER', { count: recordings.length })}</ClassCounter>
                  </LabelWrapper>
                ),
                children: <SectionRecordings recordings={recordings} />
              }
            ]}
          />
        ))}
    </Wrapper>
  );
};
